import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import useHostedMediaRuntime from 'components/shared/MediaDock/useHostedMediaRuntime'
import {
    getMediaPlaybackState,
    isElementUserVisible,
    setMediaPlaybackState,
    subscribeToMediaDock,
} from 'state/mediaPlaybackCoordinator'

/**
 * The hosted counterpart of {@link AudioPlayerCore}: it mounts no `<audio>`
 * element at all. The media dock owns the single runtime for the track and
 * this component is the bridge between it and the post's transport UI, which
 * is unchanged — same props, same ref API, same callbacks.
 *
 * Audio has no picture, so the dock hosts it "silently": it stays out of sight
 * while the post is on screen and reveals itself in the corner only once the
 * post leaves. Because the element never changes hands, navigating cannot
 * interrupt playback, re-download the file, or re-seek.
 */
/** @type {React.ForwardRefRenderFunction<any, any>} */
const HostedAudioPlayerCoreInner = (
    {
        mediaKey,
        src,
        originRef,
        autoPlay = false,
        isPaused,
        resumePlaybackToken,
        volume = 1,
        playbackRate = 1,
        onDurationChange,
        onProgressChange,
        onPlaybackError,
        onPlaybackStateChange,
        onManualPlaybackChange,
        onSnapshotChange,
        // 'visual' parks the dock's element over originRef (a video song on a
        // radio card); audio stays 'silent'.
        placement = 'silent',
        // Called with { isHostedHere, chromeLayer } so a visual host can draw
        // its controls above the parked picture.
        onHostedPresentation,
    },
    ref,
) => {
    const effectiveMediaKey = useMemo(
        () => mediaKey || (src ? `audio-attachment:${String(src)}` : null),
        [mediaKey, src],
    )

    const runtime = useHostedMediaRuntime({
        mediaKey: effectiveMediaKey,
        enabled: true,
        originRef,
        placement,
    })
    const { snapshot, refresh, play: playRuntime } = runtime

    useEffect(() => {
        onHostedPresentation?.({
            isHostedHere: runtime.isHostedHere,
            chromeLayer: runtime.chromeLayer,
        })
    }, [onHostedPresentation, runtime.isHostedHere, runtime.chromeLayer])

    // --- report state upward exactly like the element-backed core ----------

    const reportedRef = useRef({ duration: null, isPlaying: null })
    useEffect(() => {
        onSnapshotChange?.({
            currentTime: snapshot.currentTime,
            duration: snapshot.duration,
            isPlaying: snapshot.isPlaying,
        })

        const reported = reportedRef.current
        if (
            snapshot.duration > 0 &&
            (!Number.isFinite(reported.duration) ||
                Math.abs(reported.duration - snapshot.duration) > 0.01)
        ) {
            reported.duration = snapshot.duration
            onDurationChange?.(snapshot.duration)
        }
        if (snapshot.duration > 0) {
            onProgressChange?.(snapshot.currentTime, snapshot.duration)
        }
        if (reported.isPlaying !== snapshot.isPlaying) {
            reported.isPlaying = snapshot.isPlaying
            onPlaybackStateChange?.(snapshot.isPlaying)
        }
    }, [
        onDurationChange,
        onPlaybackStateChange,
        onProgressChange,
        onSnapshotChange,
        snapshot.currentTime,
        snapshot.duration,
        snapshot.isPlaying,
    ])

    useEffect(() => {
        reportedRef.current = { duration: null, isPlaying: null }
    }, [effectiveMediaKey])

    // The transport shows a duration before anything plays. The element-backed
    // core gets that from its own `preload="metadata"`; with no element, read
    // it once from a detached loader — the same metadata request — and cache it
    // in playback state. It is never registered, never audible, and is dropped
    // as soon as it reports.
    useEffect(() => {
        if (!effectiveMediaKey || !src || snapshot.duration > 0) return undefined
        if (typeof Audio === 'undefined') return undefined
        const stored = getMediaPlaybackState(effectiveMediaKey)
        if (Number.isFinite(stored?.duration) && stored.duration > 0) return undefined

        let cancelled = false
        const probe = new Audio()
        const finish = () => {
            if (cancelled) return
            cancelled = true
            const measured = probe.duration
            probe.removeEventListener('loadedmetadata', finish)
            probe.removeEventListener('error', finish)
            try {
                probe.src = ''
            } catch {}
            if (Number.isFinite(measured) && measured > 0) {
                setMediaPlaybackState(effectiveMediaKey, { duration: measured })
                refresh()
            }
        }

        probe.preload = 'metadata'
        probe.muted = true
        probe.addEventListener('loadedmetadata', finish)
        probe.addEventListener('error', finish)
        probe.src = src

        return () => {
            cancelled = true
            probe.removeEventListener('loadedmetadata', finish)
            probe.removeEventListener('error', finish)
            try {
                probe.src = ''
            } catch {}
        }
    }, [effectiveMediaKey, refresh, snapshot.duration, src])

    // --- commands ----------------------------------------------------------

    const play = useCallback(async () => {
        onManualPlaybackChange?.(true)
        if (!runtime.play({ startAtBeginning: runtime.snapshot.isEnded })) {
            onPlaybackError?.()
        }
    }, [onManualPlaybackChange, onPlaybackError, runtime])

    const pause = useCallback(() => {
        onManualPlaybackChange?.(false)
        runtime.pause()
    }, [onManualPlaybackChange, runtime])

    const togglePlayback = useCallback(async () => {
        if (runtime.snapshot.isPlaying) pause()
        else await play()
    }, [pause, play, runtime])

    // The dock's runtime just left another post playing this song as the user
    // navigated here (a swipe, or a thread sliding in). While this post is the
    // one on screen, take it back exactly as pressing play here does: the same
    // runtime is hosted over this post again and the corner dock stays hidden.
    useEffect(() => {
        if (!effectiveMediaKey) return undefined
        return subscribeToMediaDock(event => {
            if (event?.type !== 'hostedReturn' || event.mediaKey !== effectiveMediaKey) return
            const originEl = originRef?.current
            if (!originEl || (event.originEl !== originEl && !originEl.contains(event.originEl))) {
                return
            }
            if (!isElementUserVisible(originEl)) return
            playRuntime({ source: 'hosted-return' })
        })
    }, [effectiveMediaKey, originRef, playRuntime])

    useImperativeHandle(
        ref,
        () => ({
            // Nothing inline owns an element in hosted mode; callers use this
            // only to poke a local element, which no longer exists.
            getAudioElement: () => null,
            // Control already lives with the dock runtime this drives.
            claimControl: () => true,
            pause,
            play,
            seekTo: runtime.seekTo,
            togglePlayback,
        }),
        [pause, play, runtime.seekTo, togglePlayback],
    )

    // --- prop-driven playback ---------------------------------------------

    const startedRef = useRef(false)
    useEffect(() => {
        startedRef.current = false
    }, [effectiveMediaKey])

    useEffect(() => {
        if (!autoPlay || isPaused || startedRef.current) return
        startedRef.current = true
        runtime.play({ source: 'autoplay' })
    }, [autoPlay, isPaused, runtime])

    useEffect(() => {
        if (isPaused) runtime.pause()
    }, [isPaused, runtime])

    const handledResumeTokenRef = useRef(null)
    useEffect(() => {
        if (resumePlaybackToken == null) return
        if (handledResumeTokenRef.current === resumePlaybackToken) return
        handledResumeTokenRef.current = resumePlaybackToken
        runtime.play({ source: 'resume' })
    }, [resumePlaybackToken, runtime])

    // Volume and rate are shared state on the runtime, so only a real change
    // from this surface is pushed — never the initial render, which would
    // clobber whatever the dock is already set to.
    const appliedRef = useRef({ volume: null, playbackRate: null })
    useEffect(() => {
        const applied = appliedRef.current
        if (applied.volume != null && Math.abs(applied.volume - volume) > 0.001) {
            runtime.setVolume(volume)
        }
        applied.volume = volume
    }, [runtime, volume])

    useEffect(() => {
        const applied = appliedRef.current
        if (applied.playbackRate != null && applied.playbackRate !== playbackRate) {
            runtime.setPlaybackRate(playbackRate)
        }
        applied.playbackRate = playbackRate
    }, [playbackRate, runtime])

    return null
}

const HostedAudioPlayerCore = forwardRef(HostedAudioPlayerCoreInner)

export default React.memo(HostedAudioPlayerCore)
